import React from 'react'
import I18n from '../../../../helpers/I18n'
import LazyLoadBackground from '../../../common/LazyLoadBackground'
import { useRequestContext } from '../../../common/providers/RequestProvider'
import YoutubeVideoContainerPlay from '../../../common/videos/YoutubeVideoContainerPlay'
import { CaseStudy, CaseStudyContent, LegacyCaseStudy } from '../../api/Types'
import CaseStudyStat from './CaseStudyStat'
import CustomerStoryStat from './CustomerStoryStat'

interface Props {
  caseStudy?: CaseStudy | LegacyCaseStudy
}

const CaseStudyHero: React.FC<Props> = ({ caseStudy }) => {
  if (!caseStudy) return null

  const { isBot } = useRequestContext()

  let legacyContent: CaseStudyContent | null = null

  if (typeof caseStudy.content_json === 'string') {
    try {
      legacyContent = JSON.parse(caseStudy.content_json)
    } catch (e) {
      console.error(e)
      toastr.error('Failed to parse case study content.')
      return null
    }
  }

  const logo = legacyContent ? legacyContent.info?.logo : (caseStudy as CaseStudy).logo_image_url
  const quoteText = legacyContent ? legacyContent.quote?.text : (caseStudy as CaseStudy).quote_text
  const quoteAuthor = legacyContent
    ? legacyContent.quote?.author
    : (caseStudy as CaseStudy).quote_author
  const youtubeId = legacyContent
    ? legacyContent.summary?.youtube_id
    : (caseStudy as CaseStudy).youtube_id
  const title = legacyContent ? legacyContent.header?.title : (caseStudy as CaseStudy).title
  const legacyFallbackImageUrl = legacyContent?.summary?.image_url

  const url = `/customer-stories/${caseStudy.url_slug}`

  return (
    <div className='case-study-hero'>
      <div className='case-study-hero-container'>
        <h2 className='case-study-hero-title'>
          {I18n.t('views.cms.landing_pages.case_study.title')}
        </h2>
        <div className='case-study-hero-grid'>
          <div className='case-study-hero-content'>
            <div className='case-study-hero-company-logo'>
              {!isBot && !!logo && <img src={logo} alt={caseStudy.company || 'Company logo'} />}
            </div>
            {quoteText && (
              <blockquote className='case-study-hero-quote'>
                <p>{quoteText}</p>
                <span className='case-study-hero-quote-author'>
                  <span className='bold'>{quoteAuthor}</span>
                  {caseStudy.company && ` | ${caseStudy.company}`}
                </span>
              </blockquote>
            )}
            {legacyContent && (
              <div className='case-study-hero-stats'>
                <CaseStudyStat
                  stat={legacyContent.stats?.design_time}
                  defaultLabel='hrs'
                  textHelper={I18n.t('views.cms.landing_pages.case_study.stats.design')}
                />
                <CaseStudyStat
                  stat={legacyContent.stats?.assembly_time}
                  defaultLabel='hrs'
                  textHelper={I18n.t('views.cms.landing_pages.case_study.stats.assembly')}
                />
                <CaseStudyStat
                  stat={legacyContent.stats?.commissioning}
                  defaultLabel='hrs'
                  textHelper={I18n.t('views.cms.landing_pages.case_study.stats.commissioning')}
                />
              </div>
            )}
            {(caseStudy as CaseStudy).case_study_stats && (
              <div className='customer-story-stats customer-story-stats-transparent'>
                {(caseStudy as CaseStudy).case_study_stats.map((stat, index) => (
                  <CustomerStoryStat key={index} customerStoryStat={stat} />
                ))}
              </div>
            )}
          </div>
          <div className='case-study-hero-image'>
            <LazyLoadBackground
              className='case-study-hero-accent'
              backgroundUrl='https://assets.vention.io/page-assets/main-landing-page/case-study-accent.svg'
            />
            {youtubeId ? (
              <YoutubeVideoContainerPlay youtubeId={youtubeId} url={url} title={title} />
            ) : (
              !isBot &&
              !!legacyFallbackImageUrl && (
                <a href={url}>
                  <img src={legacyFallbackImageUrl} alt={title} />
                </a>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CaseStudyHero
